<script>
import { mapState, mapActions } from 'vuex';
import twConfirm from '../../components/TwConfirmDialog';

export default {
  name: 'GiftVoucherList',
  components: {
    twConfirm,
  },
  data() {
    return {
      selectedBrands: [],
      selectedDomains: [],
      lastSearch: undefined,
      voucherReference: '',
      headers: [
        { text: 'Originated Booking', value: 'booking.reference' },
        { text: 'Customer', value: 'customer.name' },
        { text: 'Voucher Ref.', value: 'giftVoucher.voucherRef' },
        { text: 'Amount', value: 'giftVoucher.amount', align: 'right' },
        { text: 'Amount Left', value: 'giftVoucher.amountAvailable', align: 'right' },
        { text: 'Currency', value: 'giftVoucher.currency' },
        { text: 'Template', value: 'giftVoucher.template.name' },
        { text: 'Issued', value: 'giftVoucher.issueDate', align: 'center' },
        { text: 'Expiration Date', value: 'giftVoucher.expirationDate', align: 'center' },
        { text: 'Action', sortable: false, width: '50px' },
      ],
      pagination: {
        rowsPerPage: 25,
        page: 1,
        sortBy: 'issueDate',
      },
      filterValue: undefined,
      cancelling: undefined,
      giftVoucherToCancel: undefined,
      cancelError: false,
      downloading: false,
      downloadError: false,
    };
  },
  computed: {
    ...mapState({
      searchLoading: state => state.giftVouchers.search.loading,
      brands: state => state.giftVouchers.search.brands.items,
      giftVouchers: state => state.giftVouchers.search.giftVouchers.items,
    }),
    filteredDomains() {
      const domains = [];
      const brandIds = this.selectedBrands;
      if (this.brands.length) {
        this.brands.forEach((brand) => {
          if (!brandIds.length || brandIds.indexOf(brand.id) !== -1) {
            brand.domains.forEach((domain) => {
              domains.push(domain);
            });
          }
        });
      }
      return domains;
    },
  },
  methods: {
    ...mapActions({
      setBreadcrumbs: 'nav/setBreadcrumbs',
      loadBrands: 'giftVouchers/loadBrands',
      searchGiftVouchers: 'giftVouchers/searchGiftVouchers',
      searchGiftVoucherByReference: 'giftVouchers/searchGiftVoucherByReference',
      cancelGiftVoucher: 'giftVouchers/cancelGiftVoucher',
      downloadGiftVouchers: 'giftVouchers/downloadGiftVouchers',
    }),
    search() {
      if (this.voucherReference.length) {
        this.searchGiftVoucherByReference(this.voucherReference);
        return;
      }
      let domainIds = this.selectedDomains;
      if (!domainIds.length) {
        domainIds = this.filteredDomains.map(domain => domain.id);
      }
      this.searchGiftVouchers(domainIds).then(() => {
        this.lastSearch = domainIds;
      });
    },
    getTwentyBookingUrl(bookingId) {
      return `${process.env.TWENTY_FRONT_PAGE}/booking/bookings/${bookingId}/info`;
    },
    confirmCancelVoucher(giftVoucher) {
      if (this.cancelling || !giftVoucher.active || giftVoucher.amountAvailable === 0) {
        return;
      }
      this.giftVoucherToCancel = giftVoucher;
      this.$refs.confirm.open();
    },
    cancelVoucher() {
      if (this.cancelling) {
        return;
      }
      this.cancelling = this.giftVoucherToCancel.id;
      this.cancelGiftVoucher(this.giftVoucherToCancel.voucherRef).catch(() => {
        this.cancelError = true;
      }).finally(() => {
        this.giftVoucherToCancel = undefined;
        this.cancelling = undefined;
      });
    },
    download() {
      if (this.downloading) {
        return;
      }
      this.downloading = true;
      this.downloadGiftVouchers(this.lastSearch).catch(() => {
        this.downloadError = true;
      }).finally(() => {
        this.downloading = false;
      });
    },
  },
  created() {
    this.setBreadcrumbs({
      breadcrumbs: [
        { text: this.$t('menu.giftVouchers.title'), to: '', disabled: true },
        { text: this.$t('menu.giftVouchers.pages.search.title'), to: this.$route.path },
      ],
      title: this.$t('menu.giftVouchers.pages.search.title'),
    });
  },
  mounted() {
    if (!this.brands.length && !this.searchLoading) {
      this.loadBrands();
    }
  },
};
</script>

<template>
  <v-flex xs12>
    <v-container grid-list-md>
      <v-layout row wrap>
        <v-flex xs12>
            <v-layout row wrap>
              <v-flex xs6 pr-5>
                <h2 class="mb-4 text-xs-center">
                  {{ $t('giftVouchers.search.search') }}
                </h2>
                <v-select
                  v-model="selectedBrands"
                  :items="brands"
                  :loading="searchLoading"
                  :disabled="searchLoading"
                  :label="$t('giftVouchers.search.brands')"
                  item-value="id"
                  item-text="name"
                  multiple
                  dense
                />
                <v-select
                  v-model="selectedDomains"
                  :items="filteredDomains"
                  :loading="searchLoading"
                  :disabled="searchLoading"
                  :label="$t('giftVouchers.search.domains')"
                  item-value="id"
                  item-text="name"
                  multiple
                  dense
                />
                <v-text-field
                  v-model="voucherReference"
                  :label="$t('giftVouchers.search.voucherRef')"
                  :hint="$t('giftVouchers.search.voucherRefHint')"
                  clearable
                />
                <div class="text-xs-right">
                  <v-btn
                    @click="search"
                    :disabled="searchLoading
                    || downloading
                    || (!selectedDomains.length
                    && !selectedBrands.length
                    && !voucherReference.length)"
                    color="info"
                    class="mt-3 mr-0"
                  >
                    {{ $t('giftVouchers.search.search') }}
                  </v-btn>
                </div>
              </v-flex>
            </v-layout>
        </v-flex>
      </v-layout>
    </v-container>
    <v-divider />
    <v-container>
      <v-layout row>
          <v-flex xs3>
            <v-text-field
              v-model="filterValue"
              append-icon="search"
              label="Filter"
              single-line
              hide-details
            />
          </v-flex>
          <v-flex xs2 offset-xs7>
            <v-tooltip left>
              <template v-slot:activator="{ on }">
                <v-btn
                  v-on="on"
                  @click="download"
                  :disabled="giftVouchers.length === 0"
                  :loading="downloading"
                  class="white--text right"
                  color="blue"
                >
                  <template>
                    <v-icon color="white">cloud_download</v-icon>
                    <span class="pl-2">{{ $t('inputs.download') }}</span>
                  </template>
                </v-btn>
              </template>
              <p>{{ $t('giftVouchers.search.downloadTooltip1') }}</p>
              <span>{{ $t('giftVouchers.search.downloadTooltip2') }}</span>
            </v-tooltip>
          </v-flex>
      </v-layout>
      <v-layout row>
        <v-flex xs12>
          <v-data-table
            :headers="headers"
            :pagination.sync="pagination"
            :items="giftVouchers"
            :loading="searchLoading"
            :search="filterValue"
          >
            <template v-slot:items="props">
              <tr :class="{
                'gift-voucher-list__inactive grey lighten-2': !props.item.giftVoucher.active ||
                props.item.giftVoucher.amountAvailable === 0
              }">
                <td>
                  <a :href="getTwentyBookingUrl(props.item.booking.id)" target="_blank">
                    {{ props.item.booking.reference }}
                  </a>
                </td>
                <td>
                  {{ props.item.customer.name }}
                  <br/>
                  <i>{{ props.item.customer.email}}</i>
                </td>
                <td>
                  {{ props.item.giftVoucher.voucherRef }}
                </td>
                <td class="text-xs-right">{{ props.item.giftVoucher.amount }}</td>
                <td class="text-xs-right">{{ props.item.giftVoucher.amountAvailable }}</td>
                <td>{{ props.item.giftVoucher.currency }}</td>
                <td>{{ props.item.giftVoucher.template.name }}</td>
                <td class="text-xs-center">
                  {{ props.item.giftVoucher.issueDate | moment('DD/MM/YY') }}
                </td>
                <td class="text-xs-center">
                  {{ props.item.giftVoucher.expirationDate | moment('DD/MM/YY') }}
                </td>
                <td class="gift-voucher-list__action">
                  <v-btn
                    @click="confirmCancelVoucher(props.item.giftVoucher)"
                    :disabled="!!cancelling ||
                      !props.item.giftVoucher.active ||
                      props.item.giftVoucher.amountAvailable === 0"
                    color="red"
                    small
                  >
                    <template v-if="cancelling === props.item.giftVoucher.id">
                      <v-progress-circular :indeterminate="true" size="20"></v-progress-circular>
                    </template>
                    <span v-else :class="{ 'white--text': props.item.giftVoucher.active &&
                      props.item.giftVoucher.amountAvailable !== 0 }">
                      {{ $t('giftVouchers.search.cancel') }}
                    </span>
                  </v-btn>
                </td>
              </tr>
            </template>
          </v-data-table>
        </v-flex>
      </v-layout>
    </v-container>
    <tw-confirm
      @confirm="cancelVoucher"
      @cancel="giftVoucherToCancel = undefined"
      :title="$t('giftVouchers.search.confirm.title')"
      :description="$t('giftVouchers.search.confirm.description')"
      ref="confirm"
    >
    </tw-confirm>
    <v-snackbar v-model="cancelError">{{ $t('giftVouchers.search.cancelError') }}</v-snackbar>
    <v-snackbar v-model="downloadError">{{ $t('giftVouchers.search.downloadError') }}</v-snackbar>
  </v-flex>
</template>

<style lang="scss">
  .gift-voucher-list {
    &__inactive td:not(.gift-voucher-list__action) {
      text-decoration-line: line-through;
    }
  }
</style>



// WEBPACK FOOTER //
// src/pages/gift-vouchers/GiftVoucherList.vue